import { useMemo } from 'react';
import {
  IBrandLocaleQuery,
  useBrandLocaleQuery,
} from '../services/brandRulesService/__generated__/queries.generated';
import { asCurrency, Currency } from '../utils/money';
import { asCountry, CountryCode } from '../utils/regions';
import { AreaUnit, DistanceUnit } from '../utils/units';

export interface BrandLocale {
  country: CountryCode;
  currency: Currency;
  measurementSystem: 'imperial' | 'metric';
  distanceUnit: DistanceUnit;
  areaUnit: AreaUnit;
}

export const DEFAULT_BRAND_LOCALE: BrandLocale = Object.freeze({
  country: 'US',
  currency: 'USD',
  measurementSystem: 'imperial',
  distanceUnit: 'mi',
  areaUnit: 'sqft',
}) as BrandLocale;

/** Spec 028: no answer, a failed request or an older API read as US, USD, imperial. */
export function brandLocaleFrom(data: IBrandLocaleQuery | null | undefined): BrandLocale {
  const rules = data?.myBrandRules;
  if (!rules) return DEFAULT_BRAND_LOCALE;
  const metric = rules.measurementSystem === 'metric';
  return {
    country: asCountry(rules.country),
    currency: asCurrency(rules.currency),
    measurementSystem: metric ? 'metric' : 'imperial',
    distanceUnit: metric ? 'km' : 'mi',
    areaUnit: metric ? 'sqm' : 'sqft',
  };
}

/** Spec 028: this site's country, currency and units (for pages that are not a trip). */
export function useBrandLocale(): BrandLocale {
  const { data } = useBrandLocaleQuery({ fetchPolicy: 'cache-first', errorPolicy: 'ignore' });
  return useMemo(() => brandLocaleFrom(data), [data]);
}
